import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DISTRICTS, NEON_SLOTS, CRASH_GAMES, LIVE_TABLES, IMAGES } from '../mock/data';
import { Reveal, Eyebrow } from '../components/xc/Bits';
import SlotDemo from '../components/xc/SlotDemo';
import HeroVideo from '../components/xc/HeroVideo';
import CrashGame from '../components/xc/CrashGame';
import LiveTable from '../components/xc/LiveTable';
import * as Icons from 'lucide-react';
import { Radio, MapPin, ArrowLeft, Play, Info, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';

const DISTRICT_CONFIG = {
  'neon-strip': {
    hero: IMAGES.rainyStreet,
    video: { src720: '/hero/neon-strip-720.mp4', src360: '/hero/neon-strip-360.mp4' },
    sections: [
      { title: 'Slots', type: 'slot', tagline: 'Reels, jackpots and neon spin the Strip until sunrise.', games: NEON_SLOTS },
      { title: 'Crash Games', type: 'crash', tagline: 'Ride the multiplier and cash out before it crashes.', games: CRASH_GAMES },
    ],
  },
  'old-quarter': {
    hero: IMAGES.foggyCity,
    video: { src720: '/hero/old-quarter-720.mp4', src360: '/hero/old-quarter-360.mp4' },
    note: 'All Old Quarter tables are small-stake — the house caps every bet at €100.',
    sections: [
      { title: 'Live Tables', type: 'table', tagline: 'Classic small-stake live casino. Maximum stake €100 per round.', games: LIVE_TABLES },
    ],
  },
};

function GameTile({ game, onPlay }) {
  const Icon = Icons[game.icon] || Icons.Dice5;
  return (
    <button onClick={onPlay} className="group relative rounded-xl overflow-hidden text-left w-full aspect-[3/4] xc-card">
      <div className="absolute inset-0" style={{ background: game.grad }} />
      <div className="absolute inset-0 xc-grid opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
      <div className="absolute top-3 left-3"><span className="text-[10px] font-600 tracking-[0.12em] uppercase px-2 py-1 rounded-full bg-black/40 text-white/90 border border-white/15">{game.tag}</span></div>
      <div className="absolute inset-0 grid place-items-center"><Icon className="h-10 w-10 text-white/85 drop-shadow-lg group-hover:scale-110 transition-transform duration-500" /></div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="text-[10px] text-white/50 uppercase tracking-wide">Provider Game — Demo</div>
        <div className="font-display font-600 text-white leading-tight">{game.title}</div>
      </div>
      <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
        <span className="btn-neon rounded-full h-12 w-12 grid place-items-center"><Play className="h-5 w-5 fill-white" /></span>
      </div>
    </button>
  );
}

export default function District() {
  const { id } = useParams();
  const nav = useNavigate();
  const district = DISTRICTS.find((d) => d.id === id);
  const config = DISTRICT_CONFIG[id];
  const [active, setActive] = useState(null); // { game, type }

  if (!district || !config) {
    return (
      <div className="min-h-[70vh] grid place-items-center text-center px-4">
        <div>
          <h1 className="font-display font-700 text-3xl text-white">This district isn't open yet</h1>
          <p className="text-white/55 mt-2">Come back soon — Xincity is still expanding.</p>
          <button onClick={() => nav('/city')} className="btn-neon rounded-xl px-6 py-3 font-600 mt-6 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back to the City</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#070609] min-h-screen">
      {/* Hero */}
      <section className="relative h-[44vh] min-h-[340px] overflow-hidden">
        <HeroVideo poster={config.hero} src720={config.video?.src720} src360={config.video?.src360} objectPosition="center" />
        <div className="absolute inset-0 city-overlay" />
        <div className="absolute inset-0 xc-grid opacity-25" />
        <div className="absolute inset-0 vignette" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 h-full flex flex-col justify-end pb-8">
          <button onClick={() => nav('/city')} className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors mb-3 w-fit"><ArrowLeft className="h-4 w-4" /> City</button>
          <Eyebrow color="#B026FF">{district.tag}</Eyebrow>
          <h1 className="font-display font-700 text-4xl md:text-6xl text-white tracking-tight xc-fade-up">{district.name.toUpperCase()}</h1>
          <p className="mt-3 text-white/65 max-w-2xl">{district.desc}</p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-[#2FBF71]"><Radio className="h-4 w-4 xc-pulse" /> {district.players} players present</div>
            <div className="flex items-center gap-2 text-sm text-white/60"><MapPin className="h-4 w-4 text-[#FF204E]" /> Xincity · {district.name}</div>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-10">
        {config.note && (
          <div className="mb-8 rounded-xl p-4 border border-[#E0B85A]/25 bg-[#E0B85A]/6 flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-[#E0B85A] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-white/70">{config.note}</p>
          </div>
        )}

        {config.sections.map((section) => (
          <div key={section.title} className="mb-12">
            <div className="flex items-end justify-between mb-4">
              <div>
                <h2 className="font-display font-700 text-xl md:text-2xl text-white flex items-center gap-2"><span className="h-5 w-1 rounded-full" style={{ background: 'linear-gradient(#FF204E,#B026FF)' }} />{section.title}</h2>
                <p className="text-sm text-white/50 mt-1">{section.tagline}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {section.games.map((game) => (
                <Reveal key={game.id}><GameTile game={game} onPlay={() => setActive({ game, type: section.type })} /></Reveal>
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-2xl p-6 xc-glass border border-white/8 flex items-start gap-3">
          <Info className="h-5 w-5 text-[#B026FF] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-white/55 leading-relaxed">Every game here is a fictional provider demo — no real money, deposits or gambling. In production, licensed providers supply the games and control all outcomes; Xincity only reacts to trusted settled events.</p>
        </div>
      </section>

      {/* Game launcher */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0D0B10] border-white/10 text-white max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto no-scrollbar">
          {active?.type === 'slot' && <SlotDemo slot={active.game} onClose={() => setActive(null)} />}
          {active?.type === 'crash' && <CrashGame game={active.game} />}
          {active?.type === 'table' && <LiveTable game={active.game} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
